import { FunctionComponent, useCallback, useEffect } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import router from 'next/router'
import styled, { css } from 'styled-components'

import config from '@config'
import useGetContentAccessStatus, {
  OPEN_CONTENT_ACCESS_STATUS,
} from '@hooks/useGetContentAccessStatus'
import useShouldShowTiunSnippet from '@hooks/useShouldShowTiunSnippet'
import TiunSnippet from '@components/Tiun/TiunSnippet'

const {
  subscriptions: { restrictedDrawerClassname },
} = config

interface StyledPianoRestrictedDrawerProps {
  shouldShowTiunSnippet: boolean
}

const StyledRestrictedBackground = styled.div`
  ${({
    theme: {
      colors: { staticBlack },
    },
  }) => css`
    position: fixed;
    top: 40px;
    height: 100%;
    width: 100%;
    pointer-events: none;
    background: linear-gradient(transparent, ${staticBlack});
    z-index: 1;
  `}
`

const StyledPianoRestrictedDrawer = styled.div`
  position: relative;
  bottom: 0;
  left: 0;
  z-index: 10000;
  width: 100dvw;
  top: 100dvh;
  transform: translate(0px, -30dvh);
  overflow: hidden;
`

const StyledPaywall = styled.div<StyledPianoRestrictedDrawerProps>`
  ${({ shouldShowTiunSnippet }) => css`
    display: block;
    ${shouldShowTiunSnippet &&
    css`
      display: none;
    `}
  `}
`

const PianoRestrictedDrawer: FunctionComponent = () => {
  const { contentBlocked } = useGetContentAccessStatus()
  const queryClient = useQueryClient()
  const shouldShowTiunSnippet = useShouldShowTiunSnippet()

  /**
   * Resets and invalidates the `ContentAccessStatus` query when a route change starts.
   *
   * ## Why is this needed?
   * - Ensures `contentBlocked` is set to `false` before any new page is rendered.
   * - Prevents a race condition caused by setting the `WrapperOfAll` component to a fixed position, which impacts
   * the scrollRestoration functionality of Next.js
   * - Uses `routeChangeStart` instead of `routeChangeComplete` to guarantee
   *   the state is reset early in the navigation cycle.
   *
   */
  const resetContentRestriction = useCallback(() => {
    queryClient.setQueryData(
      ['content-access-status'],
      OPEN_CONTENT_ACCESS_STATUS,
    )
    queryClient.invalidateQueries({
      queryKey: ['content-access-status'],
      exact: true,
    })
  }, [queryClient])

  useEffect(() => {
    router.events.on('routeChangeStart', resetContentRestriction)

    return () => {
      router.events.off('routeChangeStart', resetContentRestriction)
    }
  }, [resetContentRestriction])

  return contentBlocked ? (
    <>
      <StyledPianoRestrictedDrawer>
        <StyledPaywall
          className={`${restrictedDrawerClassname}`}
          shouldShowTiunSnippet={shouldShowTiunSnippet}
        />
      </StyledPianoRestrictedDrawer>

      <StyledPianoRestrictedDrawer>
        <TiunSnippet />
      </StyledPianoRestrictedDrawer>

      <StyledRestrictedBackground />
    </>
  ) : null
}

export default PianoRestrictedDrawer
